import { joiResolver } from "@hookform/resolvers/joi";
import Joi from "joi";
import type React from "react";
import { FormProvider, type SubmitHandler, useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { FormGroupLabelUI } from "../../../../components/FormGroupLabel";
import { InputUI } from "../../../../components/rhfs/Input";
import { Button } from "../../../../components/ui/button";
import type { ConfirmResetPassWordInput } from "../../../../hooks/authProvider/authProvider";

const MAX_RESEND_COUNT = 3;

type ResetPassWordConfirmUIProps = {
  onConfirmResetPassWord: SubmitHandler<ConfirmResetPassWordInput>;
  onResendResetPassWord: () => Promise<void>;
  isResending?: boolean;
  resendCount?: number;
  email: string;
};

const schema = Joi.object({
  authCode: Joi.string()
    .pattern(/^\d{6}$/)
    .required()
    .messages({
      "string.pattern.base": "6桁の数字を入力してください。",
      "string.empty": "認証コードを入力してください。",
    }),
  newPassword: Joi.string().min(8).required().messages({
    "string.min": "パスワードは8文字以上で入力してください。",
    "string.empty": "パスワードを入力してください。",
  }),
});

export const ResetPassWordConfirmUI: React.VFC<ResetPassWordConfirmUIProps> = ({
  onConfirmResetPassWord,
  onResendResetPassWord,
  isResending = false,
  resendCount = 0,
  email,
}) => {
  const methods = useForm<ConfirmResetPassWordInput>({
    resolver: joiResolver(schema),
  });

  const renderResendButton = () => (
    <Button
      size="lg"
      type="button"
      onClick={e => {
        e.preventDefault();
        onResendResetPassWord();
      }}
      disabled={isResending || resendCount >= MAX_RESEND_COUNT}
    >
      {isResending ? "送信中..." : "認証コードを再送"}
      {resendCount > 0 && ` (${resendCount}/${MAX_RESEND_COUNT})`}
    </Button>
  );

  return (
    <div className="mt-28 mx-auto">
      <h2 className="text-sm leading-9 text-center mb-6">
        {`${email}へ認証コードを送信しました。`}
        <br />
        6桁の認証コードを入力してください。
      </h2>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onConfirmResetPassWord)}>
          <FormGroupLabelUI>認証コード</FormGroupLabelUI>
          <InputUI
            type="text"
            registerName="authCode"
            placeholder="6桁の認証コードを入力してください"
            data-testid="reset-pass-word-confirm-auth-code"
          />
          <FormGroupLabelUI>新しいパスワード</FormGroupLabelUI>
          <InputUI
            registerName="newPassword"
            type="password"
            placeholder="新しいパスワード"
            data-testid="reset-pass-word-confirm-input-new-password"
          />
          <span className="text-nano">
            大文字、小文字、数字、記号を含めた8文字以上の英数字
          </span>
          <div className="flex flex-col items-center space-y-12 mt-12">
            <Button
              size="lg"
              type="submit"
              data-testid="reset-pass-word-confirm-submit"
            >
              パスワードを再設定
            </Button>
            {renderResendButton()}
          </div>
        </form>
      </FormProvider>
      <p className="text-center mt-5">
        認証コードの再送は最大3回までです。
        <br />
        上限を超えた場合は
        <Link
          to="/login"
          className="text-blue-400 cursor-pointer hover:opacity-80"
        >
          ログインページ
        </Link>
        からやり直してください。
      </p>
    </div>
  );
};
